/**
 * Nest SafeGeo client - always send a fresh App Bridge ID token.
 * Never send shop access tokens or SAFEGEO_INTERNAL_API_KEY from the browser.
 */
import { showToast } from "./feedback";


const API_ORIGIN = (import.meta.env.VITE_SAFEGEO_API_URL || "").replace(
  /\/$/,
  "",
);

const RETRY_HEADER = "X-Shopify-Retry-Invalid-Session-Request";

async function idToken(): Promise<string> {
  if (!window.shopify?.idToken) {
    throw new Error(
      "App Bridge unavailable - open SafeGeo from Shopify Admin (Apps → SafeGeo), not a standalone browser tab",
    );
  }
  return window.shopify.idToken();
}

function nestErrorMessage(text: string, status: number, path: string): string {
  try {
    const body = JSON.parse(text) as {
      error?: { message?: string; code?: string };
      message?: string;
    };
    return (
      body?.error?.message ||
      body?.message ||
      (body?.error?.code ? `${body.error.code}` : "") ||
      `SafeGeo API ${status} ${path}`
    );
  } catch {
    return text || `SafeGeo API ${status} ${path}`;
  }
}

export async function safegeoFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  if (!API_ORIGIN) {
    throw new Error("VITE_SAFEGEO_API_URL is not set");
  }

  const run = async (token: string) => {
    const headers = new Headers(init.headers);
    headers.set("Authorization", `Bearer ${token}`);
    if (init.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    return fetch(`${API_ORIGIN}${path}`, { ...init, headers });
  };

  const token = await idToken();
  let res = await run(token);

  if (res.status === 401 && res.headers.get(RETRY_HEADER)) {
    res = await run(await idToken());
  }

  if (!res.ok) {
    const text = await res.text();
    throw new Error(nestErrorMessage(text, res.status, path));
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export type SafegeoStatus = {
  shop_id?: string;
  plan?: string;
  plan_key?: string;
  needs_plan_pick?: boolean;
  plan_selected?: boolean;
  shop_domain?: string;
  /** Custom storefront host when set (prefer over myshopify handle). */
  primary_domain?: string | null;
  billing_interval?: string;
  trial_ends_at?: string | null;
  /** Set once a paid trial was started - Free → paid later has no second trial. */
  trial_used_at?: string | null;
  trial_days?: number;
  /** Shop contact (Crisp / support) - from Shopify when cached. */
  contact_email?: string | null;
  contact_name?: string | null;
  embed_active?: boolean;
  multi_store_unlocked?: boolean;
  primary_shop_id?: string | null;
  network_pending?: boolean;
  redirects_month?: number;
  redirects_cap?: number | null;
  redirects_pct?: number | null;
  redirects_at_cap?: boolean;
  redirects_near_cap?: boolean;
  redirects_grace_until?: string | null;
  rules_published?: number;
  design_configured?: boolean;
  last_published_at?: string | null;
  /** Server-backed storefront preview (Final P0 #6). */
  last_tested_at?: string | null;
  /** False when SAFEGEO_WORKER_URL missing. */
  worker_configured?: boolean;
  /** Nest preview-verify available (API_PUBLIC_URL) - Open in store can work without Worker. */
  preview_verify_configured?: boolean;
  /** U7: Online Store password page on (null = unknown). */
  password_enabled?: boolean | null;
  /** Legacy alias of last_visit_at. */
  last_heartbeat_at?: string | null;
  /** Logic §13 visit ping. */
  last_visit_at?: string | null;
  last_visit_country?: string | null;
  visits_today?: number;
  visits_today_day?: string | null;
  seen_status?: "on" | "not_seen" | "stale";
  theme_style_detected?: boolean;
  /** X1: theme.id changed since last embed sync - re-enable in theme editor. */
  theme_changed?: boolean;
  /** no visit ping in 24h while embed_active (null = unknown / never expected). */
  heartbeat_stale?: boolean | null;
  created_at?: string;
  settings?: {
    setup_step2_at?: string;
    markets_enabled?: boolean;
    markets_auto?: boolean;
    setup_preset?: string;
    market_countries?: string[];
    market_currencies?: Record<string, string>;
    /** Primary (+ optional comma-separated alternates) per country. */
    market_languages?: Record<string, string>;
    /** v7 §23: New/Removed since last sync (dismiss via markets/acknowledge). */
    markets_added?: string[];
    markets_removed?: string[];
    paused?: boolean;
    email_alerts?: {
      embed_down?: boolean;
      near_cap?: boolean;
      join_request?: boolean;
      near_cap_pct?: 50 | 80;
    };
  };
  entitlements?: {
    plan_key?: string;
    rules_max?: number;
    features?: string[];
    stats_days?: number;
  };
};

export type SafegeoRule = {
  id: string;
  countries: string[];
  destination_url: string | null;
  action: string;
  is_enabled: boolean;
  is_fallback: boolean;
  sort_order?: number;
  keep_path?: boolean;
  keep_query?: boolean;
  /** Block of EEA country: merchant confirmed legal requirement. */
  legal?: boolean;
};

export type MarketsPayload = {
  countries: string[];
  labels: string[];
  currencies: Record<string, string>;
  /** Primary (+ optional comma-separated alternates) per country. */
  languages?: Record<string, string>;
  count: number;
  shopify_plus: boolean;
  shop_currency?: string;
  error?: string;
  synced?: boolean;
  published?: boolean;
  /** v7 §23 sync deltas (POST /markets/sync). */
  added?: string[];
  removed?: string[];
};

export function bootstrapShop(body: Record<string, unknown> = {}) {
  return safegeoFetch<{ shop?: { plan_key?: string; myshopify_domain?: string } }>(
    "/api/v1/safegeo/bootstrap",
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function getStatus() {
  return safegeoFetch<SafegeoStatus>("/api/v1/safegeo/status");
}

export function syncBilling(body: {
  plan_key: string;
  billing_interval?: "month" | "year";
  plan_handle?: string | null;
}) {
  return safegeoFetch<{
    plan_key?: string;
    pricing_hint?: string | null;
    shop?: { plan_key?: string };
  }>("/api/v1/safegeo/billing/sync", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function listRules() {
  return safegeoFetch<{ items: SafegeoRule[] }>("/api/v1/safegeo/rules");
}

export function getRule(id: string) {
  return safegeoFetch<SafegeoRule>(`/api/v1/safegeo/rules/${id}`);
}

export function createRule(body: Record<string, unknown>) {
  return safegeoFetch<SafegeoRule>("/api/v1/safegeo/rules", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateRule(id: string, body: Record<string, unknown>) {
  return safegeoFetch<SafegeoRule>(`/api/v1/safegeo/rules/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export function deleteRule(id: string) {
  return safegeoFetch(`/api/v1/safegeo/rules/${id}`, { method: "DELETE" });
}

export function reorderRule(id: string, direction: "up" | "down") {
  return safegeoFetch(`/api/v1/safegeo/rules/${id}/reorder`, {
    method: "POST",
    body: JSON.stringify({ direction }),
  });
}

export function updateSettings(body: Record<string, unknown>) {
  return safegeoFetch("/api/v1/safegeo/settings", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export type PublishResult = {
  metafield_ok?: boolean;
  metafield_error?: string | null;
  skipped?: boolean;
  message?: string | null;
  hash?: string;
};

export function publishConfig(opts?: { first_publish?: boolean }) {
  return safegeoFetch<PublishResult>("/api/v1/safegeo/publish", {
    method: "POST",
    body: JSON.stringify({
      first_publish: !!opts?.first_publish,
    }),
  });
}

/** v7 §15.3: look up SG-XXXX from Block / Settings. */
export function lookupDecision(code: string) {
  const q = new URLSearchParams({ code: code.trim() });
  return safegeoFetch<{
    found: boolean;
    code?: string;
    country_code?: string;
    event?: string;
    is_test?: boolean;
    path?: string | null;
    created_at?: string;
    message?: string;
  }>(`/api/v1/safegeo/decisions/lookup?${q}`);
}

export function acknowledgeMarketChanges() {
  return safegeoFetch<{
    ok: boolean;
    markets_added: string[];
    markets_removed: string[];
  }>("/api/v1/safegeo/markets/acknowledge", {
    method: "POST",
    body: "{}",
  });
}

/**
 * Toast only on success (BFS 4.2.4 - never toast errors).
 * Returns ok=false + message when publish pending/failed so callers can show ErrorBanner.
 */
export function toastPublishResult(
  result: PublishResult | null | undefined,
  okFallback = "Saved and live",
): { ok: boolean; message?: string } {
  if (!result) {
    return {
      ok: false,
      message: "Saved, but storefront publish is still pending. Try Publish again.",
    };
  }
  if (result.metafield_ok === false) {
    return {
      ok: false,
      message: result.metafield_error
        ? `Saved, but storefront publish failed: ${result.metafield_error}`
        : "Saved, but storefront publish failed - try Publish again",
    };
  }
  showToast(result.message || (result.skipped ? "Already live" : okFallback));
  return { ok: true };
}

export function applySetup(body: {
  preset: "market" | "multi" | "block";
  market_countries?: string[];
  markets_count?: number;
}) {
  return safegeoFetch<{
    ok?: boolean;
    next?: string;
    published?: boolean;
  }>("/api/v1/safegeo/setup/apply", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function setEmbedActive(active: boolean) {
  return safegeoFetch<{ embed_active: boolean }>("/api/v1/safegeo/embed", {
    method: "POST",
    body: JSON.stringify({ active }),
  });
}

export function getEmbedStatus() {
  return safegeoFetch<{ embed_active: boolean | null }>(
    "/api/v1/safegeo/embed/status",
  );
}

export function getMarkets() {
  return safegeoFetch<MarketsPayload>("/api/v1/safegeo/markets");
}

/** Pull Shopify Markets into settings; publishes when Market redirect is on. */
export function syncMarkets() {
  return safegeoFetch<MarketsPayload>("/api/v1/safegeo/markets/sync", {
    method: "POST",
  });
}

export function createPreviewLink(country?: string) {
  return safegeoFetch<{
    url: string;
    country: string;
    expires_in: number;
    token: string;
  }>("/api/v1/safegeo/preview-link", {
    method: "POST",
    body: JSON.stringify({ country: country || "CA" }),
  });
}

export function simulateVisit(body: {
  country: string;
  href?: string;
}) {
  return safegeoFetch<{
    decision?: {
      kind?: string;
      reasons?: string[];
      target?: string | null;
    };
    country?: string;
  }>("/api/v1/safegeo/simulate", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export type SafegeoStatsDay = {
  day: string;
  redirected: number;
  suggested: number;
  stayed: number;
  switched?: number;
  blocked: number;
  unique_users: number;
};

export type SafegeoStatsCountry = {
  country: string;
  redirected: number;
  suggested: number;
  stayed: number;
  switched?: number;
  blocked: number;
  visitors: number;
};

export type SafegeoStatsRecent = {
  at: string;
  country: string;
  what: string;
  reason: string | null;
  path: string | null;
  ref: string | null;
  is_test: boolean;
  /** @deprecated daily rollup shape - keep optional for older API */ 
  day?: string;
  redirected?: number;
  suggested?: number;
  stayed?: number;
  blocked?: number;
};

export function getStats(opts?: {
  days?: number;
  month?: string;
  /** live (default) = real shoppers; preview = geo_test / Open in your store */
  traffic?: "live" | "preview";
}) {
  const params = new URLSearchParams();
  if (opts?.month) params.set("month", opts.month);
  else if (opts?.days != null) params.set("days", String(opts.days));
  if (opts?.traffic === "preview") params.set("traffic", "preview");
  const q = params.toString() ? `?${params}` : "";
  return safegeoFetch<{
    days?: number;
    month?: string | null;
    since?: string;
    until?: string;
    stats_days_max?: number;
    traffic?: "live" | "preview";
    totals?: {
      redirected?: number;
      suggested?: number;
      stayed?: number;
      switched?: number;
      blocked?: number;
      unique_users?: number;
    };
    by_day?: SafegeoStatsDay[];
    by_country?: SafegeoStatsCountry[];
    recent?: SafegeoStatsRecent[];
  }>(`/api/v1/safegeo/stats${q}`);
}

/** Full plan catalog - only for “manage / switch to Free” (not per-plan CTAs). */
export function shopifyPricingPlansUrl(
  shopDomain: string,
  appHandle = "safegeo",
) {
  const handle = shopDomain.replace(/\.myshopify\.com$/i, "");
  return `https://admin.shopify.com/store/${encodeURIComponent(handle)}/charges/${encodeURIComponent(appHandle)}/pricing_plans`;
}

/**
 * Nest `plan_key` (= Partner Internal handle, kebab).
 * Strip `-yearly` / legacy `_`; charge URL uses base handle + interval.
 */
export function partnerPlanHandle(planKey: string): string {
  const base = planKey
    .trim()
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/-yearly$/i, "");
  if (base.includes("multi")) return "safegeo-multi-store";
  if (base.includes("starter")) return "safegeo-starter";
  if (base.includes("scale")) return "safegeo-scale";
  if (base.includes("free")) return "safegeo-free";
  return base.startsWith("safegeo-") ? base : `safegeo-${base}`;
}

/**
 * One-plan charge confirm (avoids picking again on /pricing_plans).
 * e.g. /charges/safegeo-1/plans/safegeo-starter?interval=ANNUAL
 */
export function shopifyPlanChargeUrl(
  shopDomain: string,
  appHandle: string,
  planKey: string,
  interval: "month" | "year",
) {
  const store = shopDomain.replace(/\.myshopify\.com$/i, "");
  const planHandle = partnerPlanHandle(planKey);
  const iv = interval === "year" ? "ANNUAL" : "EVERY_30_DAYS";
  return `https://admin.shopify.com/store/${encodeURIComponent(store)}/charges/${encodeURIComponent(appHandle)}/plans/${encodeURIComponent(planHandle)}?interval=${iv}`;
}

export function themeEmbedDeepLink(shopDomain: string, apiKey: string) {
  const handle = shopDomain.replace(/\.myshopify\.com$/i, "");
  return `https://admin.shopify.com/store/${encodeURIComponent(handle)}/themes/current/editor?context=apps&activateAppId=${encodeURIComponent(apiKey)}/safegeo-embed`;
}

/** Online Store → Preferences (Automatic redirection lives here). */
export function onlineStorePreferencesDeepLink(shopDomain: string) {
  const handle = shopDomain.replace(/\.myshopify\.com$/i, "");
  return `https://admin.shopify.com/store/${encodeURIComponent(handle)}/online_store/preferences`;
}

/** App Store listing / install link for secondary stores (U15). */
export function shopifyAppInstallUrl(appHandle = "safegeo-1") {
  return `https://apps.shopify.com/${encodeURIComponent(appHandle)}`;
}

export function shopifyAppInstallMailto(opts: {
  appHandle?: string;
  primaryDomain?: string;
}) {
  const link = shopifyAppInstallUrl(opts.appHandle);
  const subject = encodeURIComponent("Install SafeGeo on your other store");
  const body = encodeURIComponent(
    [
      "Install SafeGeo on your other Shopify store, then open the app → Store redirect → Secondary store.",
      opts.primaryDomain
        ? `Join this primary: ${opts.primaryDomain}`
        : "Join using your primary store’s domain or Store linking code.",
      "",
      link,
    ].join("\n"),
  );
  return `mailto:?subject=${subject}&body=${body}`;
}

export type SafegeoSettings = {
  cookie_days?: number;
  hide_until_decide?: boolean;
  eu_safe?: boolean;
  ads_safe?: boolean;
  visitor_stats?: boolean;
  paused?: boolean;
  plan?: string;
  staff_link?: string | null;
  excluded_paths?: string[];
  account_id?: string;
  contact_email?: string | null;
  contact_name?: string | null;
  email_alerts?: {
    embed_down?: boolean;
    near_cap?: boolean;
    join_request?: boolean;
    near_cap_pct?: 50 | 80;
  };
};

export type SafegeoDesign = {
  style?: "bar" | "popup" | "corner" | "center";
  language?: string;
  match_theme?: boolean;
  same_for_all?: boolean;
  title?: string;
  message?: string;
  button?: string;
  stay_link?: string;
  button_color?: string;
  reason_1?: string;
  reason_2?: string;
  css?: string;
  corner_radius?: number;
  show_flag?: boolean;
  show_currency?: boolean;
  show_language?: boolean;
  country_order?: "alpha" | "market";
  show_blur?: boolean;
  button_style?: "rounded" | "square" | string;
  button_align?: "left" | "stretch" | "right" | string;
  title_size?: number;
  text_size?: number;
  close_color?: string;
  cta_text_color?: string;
  text_color?: string;
  card_bg?: string;
  block_title?: string;
  block_message?: string;
  block_show_contact?: boolean;
};

export type NetworkStore = {
  shop_id: string;
  shop_domain: string;
  role: string;
  plan: string;
  status: string;
  serves_countries?: string[];
  storefront_url?: string;
  network_grace_until?: string | null;
  /** Theme app embed on that store (server last-known). */
  embed_active?: boolean;
};

export function getSettings() {
  return safegeoFetch<SafegeoSettings>("/api/v1/safegeo/settings");
}

export function rotateStaffLink() {
  return safegeoFetch("/api/v1/safegeo/settings/staff-link/rotate", {
    method: "POST",
    body: "{}",
  });
}

export function getDesign() {
  return safegeoFetch<SafegeoDesign>("/api/v1/safegeo/design");
}

export function updateDesign(body: Record<string, unknown>) {
  return safegeoFetch("/api/v1/safegeo/design", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export function listStores() {
  return safegeoFetch<{
    items?: NetworkStore[];
    is_primary?: boolean;
    pending?: boolean;
  }>("/api/v1/safegeo/stores");
}

export function createLinkCode() {
  return safegeoFetch<{ code: string; expires_at: string }>(
    "/api/v1/safegeo/stores/link-code",
    { method: "POST", body: "{}" },
  );
}

export function consumeLinkCode(code: string) {
  return safegeoFetch<{
    pending?: boolean;
    linked?: boolean;
    message?: string;
  }>("/api/v1/safegeo/stores/link", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

/** v3.2 URL join - secondary enters primary storefront / myshopify domain. */
export function joinByPrimaryUrl(primaryUrl: string) {
  return safegeoFetch<{
    pending?: boolean;
    linked?: boolean;
    message?: string;
    primary_name?: string;
    primary_shop?: string;
  }>("/api/v1/safegeo/stores/join-by-url", {
    method: "POST",
    body: JSON.stringify({ primary_url: primaryUrl }),
  });
}

/** Plans U16 - same owner email as a Multi/Scale primary. */
export function getJoinHint() {
  return safegeoFetch<{
    suggest: boolean;
    primary_name?: string;
    primary_domain?: string;
    primary_shop_id?: string;
  }>("/api/v1/safegeo/network/join-hint");
}

export function approveAllNetworkMembers() {
  return safegeoFetch<{
    approved?: number;
    failed?: number;
  }>("/api/v1/safegeo/stores/approve-all", {
    method: "POST",
    body: "{}",
  });
}

export function assignStoreServes(body: {
  shop_id: string;
  serves_countries: string[];
  storefront_url?: string;
}) {
  return safegeoFetch("/api/v1/safegeo/stores/assign", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function leaveNetwork() {
  return safegeoFetch("/api/v1/safegeo/stores/leave", {
    method: "POST",
    body: "{}",
  });
}

export function publishToAllStores() {
  return safegeoFetch<{
    results?: Array<{ shop_domain: string; ok: boolean; error?: string }>;
    ok_count?: number;
  }>("/api/v1/safegeo/stores/publish-all", {
    method: "POST",
    body: "{}",
  });
}

export function approveNetworkMember(shopId: string) {
  return safegeoFetch("/api/v1/safegeo/stores/approve", {
    method: "POST",
    body: JSON.stringify({ shop_id: shopId }),
  });
}

export function declineNetworkMember(shopId: string) {
  return safegeoFetch("/api/v1/safegeo/stores/decline", {
    method: "POST",
    body: JSON.stringify({ shop_id: shopId }),
  });
}

export function listConfigVersions() {
  return safegeoFetch<{
    items?: Array<{
      id: string;
      version: number;
      hash: string;
      size_bytes: number;
      reason: string;
      published_at: string;
      current?: boolean;
    }>;
    config_history?: number;
  }>("/api/v1/safegeo/versions");
}

export function restoreConfigVersion(versionId: string) {
  return safegeoFetch(`/api/v1/safegeo/versions/${versionId}/restore`, {
    method: "POST",
    body: "{}",
  });
}

export function logReviewRequest() {
  return safegeoFetch("/api/v1/safegeo/reviews/request-log", {
    method: "POST",
    body: "{}",
  });
}
